import "@fontsource/plus-jakarta-sans/700.css";
import "@fontsource/plus-jakarta-sans/800.css";
import { keyframes } from "@emotion/react";
import {
  Box,
  Button,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { datadogRum } from "@datadog/browser-rum";
import React, { useEffect, useRef, useState } from "react";
import { UserFull } from "../../models";
import { pendingWhatsNewEntry } from "./content";

const keyWhatsNewLastSeenId = "whats-new-last-seen-id";

// Website entrance animation (hero-enter): fade in + 10px rise.
const heroEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
`;

const jakarta = "'Plus Jakarta Sans', system-ui, sans-serif";

const WhatsNewModal = ({
  user,
  suppressed = false,
}: {
  user: UserFull | undefined;
  // Hidden while the billing dialog is due, so the two don't stack.
  suppressed?: boolean;
}) => {
  // Deliberately not the useLocalStorage hook: it JSON-serializes and
  // converts date-like strings — the entry id is a plain string.
  const [lastSeenId, setLastSeenIdState] = useState<string | undefined>(
    () => localStorage.getItem(keyWhatsNewLastSeenId) ?? undefined
  );
  const setLastSeenId = (id: string) => {
    try {
      localStorage.setItem(keyWhatsNewLastSeenId, id);
    } catch {
      // Safari Private Mode / quota: still close the dialog, it then
      // reappears at most once per session.
    }
    setLastSeenIdState(id);
  };
  const shownTracked = useRef(false);

  const entry = suppressed
    ? undefined
    : pendingWhatsNewEntry(user?.createdAt, lastSeenId);

  useEffect(() => {
    if (entry && !shownTracked.current) {
      shownTracked.current = true;
      datadogRum.addAction("WhatsNewShown", { id: entry.id });
    }
  }, [entry]);

  if (!entry) return null;

  const onClose = () => {
    setLastSeenId(entry.id);
    datadogRum.addAction("WhatsNewDismissed", { id: entry.id });
  };

  return (
    <Modal isOpen onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        borderRadius="12px"
        border="1px solid"
        borderColor="#E2E6EC"
        maxWidth="480px"
        marginX="16px"
      >
        <ModalHeader paddingBottom="0">
          <Text
            color="primary.500"
            fontFamily={jakarta}
            fontSize="12px"
            fontWeight={800}
            letterSpacing="0.05em"
            textTransform="uppercase"
          >
            Neu
          </Text>
          <Text
            color="gray.900"
            fontFamily={jakarta}
            fontSize="22px"
            fontWeight={700}
            letterSpacing="-0.015em"
          >
            Was ist neu bei Expensly?
          </Text>
        </ModalHeader>
        <ModalCloseButton data-testid="whats-new-close" />
        <ModalBody>
          {entry.sections.map((section, index) => (
            <Box
              key={section.title}
              paddingTop={index === 0 ? "8px" : "20px"}
              sx={{
                animation: `${heroEnter} 500ms cubic-bezier(0.22, 1, 0.36, 1) both`,
                animationDelay: `${index * 120}ms`,
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                },
              }}
            >
              {section.image && (
                <Image
                  src={section.image}
                  alt={section.title}
                  width="100%"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="#E2E6EC"
                  marginBottom="8px"
                />
              )}
              <Text
                color="gray.900"
                fontFamily={jakarta}
                fontSize="16px"
                fontWeight={700}
              >
                {section.title}
              </Text>
              <Text color="gray.600" fontSize="14px" paddingTop="4px">
                {section.body}
              </Text>
            </Box>
          ))}
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="primary" width="100%" onClick={onClose}>
            Verstanden
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WhatsNewModal;
